import { ArrowPathIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { memo, useCallback, useState } from 'react';
import { ToastType, useToastNotifications } from '../../hooks/useToastNotifications';
import { useT } from '../../translation/src';
import { useSuperadminRefreshInvoicesMutation } from '../../types/graphqlTypes';

interface RefreshInvoicesButtonProps {
  organisationId: string;
  // Reloads the rows once the server forgot what it cached (the history's refetch).
  onRefreshed: () => Promise<unknown>;
}

// Asks Odoo again about an organisation's invoices: what the server cached of them (payment status, number)
// is dropped first, or the rows would come back as they were a few minutes ago. For the superadmin's
// histories, next to their title.
const RefreshInvoicesButton_: React.FC<RefreshInvoicesButtonProps> = ({ organisationId, onRefreshed }) => {
  const { showToast } = useToastNotifications();
  const refreshFailed = useT('could not refresh', { swc: true });
  const refreshLabel = useT('refresh', { swc: true });
  const [refreshInvoices] = useSuperadminRefreshInvoicesMutation();
  const [refreshing, setRefreshing] = useState(false);
  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await refreshInvoices({ variables: { organisationId } });
      await onRefreshed();
    } catch (e) {
      showToast({ title: refreshFailed, text: e instanceof Error ? e.message : String(e), type: ToastType.ERROR });
    } finally {
      setRefreshing(false);
    }
  }, [refreshInvoices, onRefreshed, organisationId, showToast, refreshFailed]);

  return (
    <button
      type="button"
      onClick={onRefresh}
      disabled={refreshing}
      title={refreshLabel}
      aria-label={refreshLabel}
      className="shrink-0 cursor-pointer text-gray-500 transition-colors hover:text-black disabled:cursor-wait dark:text-gray-400 dark:hover:text-white"
    >
      <ArrowPathIcon className={classNames('w-4 h-4', refreshing && 'animate-spin')} />
    </button>
  );
};

export const RefreshInvoicesButton = memo(RefreshInvoicesButton_);
